import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function ContractManager({ selectedApp }) {

  const [contracts, setContracts] = useState([]);
  const [newContract, setNewContract] = useState({ name: '', plan_id: '' });
  const [pbps, setPbps] = useState({});
  const [newPbp, setNewPbp] = useState({});
  const [message, setMessage] = useState('');

  const loadContracts = async () => {
    try {
      const res = await fetch(`${API_BASE}/contracts`, { credentials: 'include' });
      const raw = await res.text();
      const data = JSON.parse(raw);
      if (data.success) setContracts(data.contracts);
    } catch (err) {
      console.error('❌ Failed to load contracts:', err.message);
    }
  };

  const loadPbps = async (contractId) => {
    try {
      const res = await fetch(`${API_BASE}/contracts/${contractId}/pbps`, {
        credentials: 'include',
      });
      const raw = await res.text();
      const data = JSON.parse(raw);
      if (data.success) {
        setPbps((prev) => ({ ...prev, [contractId]: data.pbps }));
      }
    } catch (err) {
      console.error(`❌ Failed to load PBPs for ${contractId}:`, err.message);
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const handleAddContract = async () => {
    if (!newContract.name.trim() || !newContract.plan_id.trim()) {
      setMessage('⚠️ Both name and plan ID are required.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newContract),
      });
      const data = await res.json();
      if (data.success) {
        setContracts((prev) => [...prev, data.contract]);
        setNewContract({ name: '', plan_id: '' });
        setMessage('✅ Contract added.');
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      console.error('❌ Failed to add contract:', err.message);
      setMessage('❌ Failed to add contract');
    }
  };

  const handleAddPbp = async (contractId) => {
    const pbpValue = newPbp[contractId]?.trim();
    if (!pbpValue) return;

    try {
      const res = await fetch(`${API_BASE}/contracts/${contractId}/pbps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pbp_id: pbpValue }),
      });

      const data = await res.json();
      if (data.success) {
        await loadPbps(contractId);
        setNewPbp((prev) => ({ ...prev, [contractId]: '' }));
        setMessage('✅ PBP added.');
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      console.error('❌ Failed to add PBP:', err.message);
      setMessage('❌ Failed to add PBP');
    }
  };
if (selectedApp !== 'memo') return null;

return (
  <div className="space-y-8 max-w-4xl mx-auto">


<h2 className="text-2xl font-semibold text-gray-800 tracking-tight">📄 Contract & PBP Manager</h2>
<p className="text-sm text-gray-500 mt-1">Manage your organization's contracts and plan benefit packages (PBPs).</p>

<div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-2">➕ Add Contract</h3>
<div className="grid grid-cols-12 gap-4">
    <input
  type="text"
  placeholder="Contract Name"
  value={newContract.name}
  onChange={(e) => setNewContract({ ...newContract, name: e.target.value })}
  className="col-span-5 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
/>

<input
  type="text"
  placeholder="Plan ID"
  value={newContract.plan_id}
  onChange={(e) => setNewContract({ ...newContract, plan_id: e.target.value })}
  className="col-span-4 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
/>

<button
  onClick={handleAddContract}
  className="col-span-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded px-4 py-2"
>
  Save Contract
</button>

        </div>
        <div className="text-sm text-gray-700 mt-2">{message}</div>
      </div>

<div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
<h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center gap-2">
  <span className="text-orange-500">📂</span> Existing Contracts
</h3>

        {contracts.length === 0 ? (
<p className="text-gray-500 text-sm italic">No contracts available yet.</p>
        ) : (
<ul className="space-y-6">

            {contracts.map((c) => (
<li key={c.id} className="border-b border-gray-200 pb-3">

                <div className="font-medium text-blue-700">
                  {c.name} ({c.plan_id})
                </div>

                {/* Load PBPs only when not loaded */}
                {!pbps[c.id] && (
<button
  onClick={() => loadPbps(c.id)}
  className="mt-2 text-sm text-blue-500 hover:underline"
>
  🔽 Load PBPs
</button>

                )}

                {pbps[c.id] && (
<ul className="mt-2 ml-4 list-disc text-sm text-gray-700">

                    {pbps[c.id].length === 0 && <li>No PBPs found.</li>}
                    {pbps[c.id].map((pbp) => (
                      <li key={pbp}>{pbp}</li>
                    ))}
                  </ul>
                )}

<div className="flex items-center gap-2 mt-3 ml-4">
 <input
  type="text"
  placeholder="Add PBP"
  value={newPbp[c.id] || ''}
  onChange={(e) =>
    setNewPbp((prev) => ({ ...prev, [c.id]: e.target.value }))
  }
  className="border border-gray-300 px-3 py-1 rounded text-sm w-52 focus:ring-2 focus:ring-green-500"
/>
<button
  onClick={() => handleAddPbp(c.id)}
  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
>
  ➕ Add
</button>

                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
